import React, { createContext, useContext, useState, useCallback } from 'react';
import { QuickCaptureModal } from './QuickCaptureModal';
import { useGlobalShortcuts } from '../../hooks/useGlobalShortcuts';
import { Box, Typography, Fade } from '@mui/material';

interface QuickCaptureContextType {
  openQuickCapture: () => void;
}

const QuickCaptureContext = createContext<QuickCaptureContextType | null>(null);

export const useQuickCapture = () => {
  const context = useContext(QuickCaptureContext);
  if (!context) {
    throw new Error('useQuickCapture must be used within QuickCaptureProvider');
  }
  return context;
};

interface QuickCaptureProviderProps {
  children: React.ReactNode;
}

export const QuickCaptureProvider: React.FC<QuickCaptureProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const openQuickCapture = useCallback(() => setIsOpen(true), []);

  // Register global Cmd/Ctrl+J shortcut
  useGlobalShortcuts({ onQuickCapture: openQuickCapture });

  const handleSuccess = () => {
    setSnackbar({ open: true, message: 'Note captured!' });
    // Auto-hide after 2 seconds
    setTimeout(() => {
      setSnackbar({ open: false, message: '' });
    }, 2000);
  };

  return (
    <QuickCaptureContext.Provider value={{ openQuickCapture }}>
      {children}
      <QuickCaptureModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSuccess={handleSuccess}
      />
      <Fade in={snackbar.open} timeout={300}>
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            p: 2,
            px: 3,
            border: '1px solid',
            borderColor: 'success.main',
            borderRadius: 2,
            bgcolor: 'success.light',
            color: 'success.dark',
            boxShadow: 3,
            display: snackbar.open ? 'block' : 'none',
          }}
        >
          <Typography variant="body1" fontWeight={600}>
            {snackbar.message}
          </Typography>
        </Box>
      </Fade>
    </QuickCaptureContext.Provider>
  );
};

export default QuickCaptureProvider;
